<script setup lang="ts">
type HtmlContentProps = {
  pageContent?: string
}

withDefaults(defineProps<HtmlContentProps>(), {
  pageContent: '',
})
</script>

<template>
  <div class="html-content" data-test-id="html-content" v-html="pageContent" />
</template>

<style scoped lang="scss">
.html-content {
  font-size: 14px;
  font-weight: 500 !important;
  line-height: 1.4 !important;
  color: var(--ui-color-supporting-01) !important;

  :deep(h1) {
    margin-bottom: 32px;
    font-size: 30px !important;

    @include media($bp-desktop-mini) {
      margin-bottom: 40px;
      font-size: 40px !important;
    }
  }

  :deep(h2),
  :deep(h3) {
    margin: 30px 0;
    font-size: 14px !important;

    @include media($bp-tablet) {
      font-size: 16px !important;
    }
  }

  :deep(.v-h2-big) {
    font-size: 16px !important;

    @include media($bp-desktop-mini) {
      font-size: 22px !important;
    }
  }

  :deep(p) {
    margin: 20px 0 0;
    font-size: 14px;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(b),
  :deep(strong) {
    font-weight: 800 !important;
  }

  :deep(ol) {
    list-style: none;
    counter-reset: li;

    li::before {
      font-weight: 800 !important;
      content: counters(li, '.') '. ';
      counter-increment: li;
    }
  }

  :deep(ul) {
    padding-left: 16px;
    list-style-type: disc;

    li {
      margin: 16px 0;
    }
  }

  :deep(img) {
    width: 24px;
    height: 24px;
    margin-right: 10px;
  }

  :deep(table) {
    table-layout: fixed;
    border-collapse: collapse;

    thead {
      border-bottom: 1px solid var(--ui-color-neutral-200);
    }

    tbody {
      tr:first-child {
        height: 10px;
      }
    }

    th {
      height: 48px;
      font-weight: 800;
    }

    td {
      height: 36px;
    }

    th,
    td {
      width: 100%;

      &:not(:last-child) {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }

  :deep(.v-table-important) {
    width: 100%;
    max-width: 634px;

    th:first-child,
    td:first-child {
      text-align: left;
    }

    th:last-child,
    td:last-child {
      text-align: center;
    }
  }

  :deep(.v-margin-bottom-20) {
    margin-bottom: 20px !important;
  }

  :deep(.v-margin-bottom-30) {
    margin-bottom: 30px !important;
  }
  :deep(.v-margin-bottom-40) {
    margin-bottom: 40px !important;
  }

  :deep(.v-margin-y-20) {
    margin: 20px 0 !important;
  }

  :deep(.v-flex-container) {
    display: flex;
    gap: 16px;
    align-items: center;
  }
}
</style>
